import clsx from "clsx";
import Head from "next/head";
import { FunctionComponent, useMemo, useRef, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { DuetPageProps } from "@voxmedia/duet-framework/types/pages";
import { DataCentersHubQuery } from "src/__generated__/relay/DataCentersHubQuery.graphql";
import {
  ArticleStoryChrome,
  ArticleStorySuspense,
} from "src/components/article-story";
import { createGetDevWarnings } from "src/config/devWarnings";

import ArticleLoadingFallback from "./components/ArticleLoadingFallback";
import HubPrivacyBanner from "./components/HubPrivacyBanner";
import PackageNav from "./components/PackageNav";
import SlideDeck, { useHubSlideState } from "./components/SlideDeck";
import { credits } from "./config/credits";
import {
  refreshLiveMetadata,
  sections,
  SKIP_TARGET_ID,
  storyTheme,
} from "./config/constants";
import { editorNoteBlocksFromNode } from "./config/editorNoteBlocks";
import type { DeckSlideId } from "./config/slides";
import * as fonts from "./config/Fonts.css";
import * as globalStyles from "./config/GlobalStyles.css";
import { ARIZONA_FEATURE_NODE_ID } from "./config/arizonaAssets";
import { LOUDOUN_FEATURE_NODE_ID } from "./config/loudounAssets";
import { UTAH_FEATURE_NODE_ID } from "./config/utahAssets";

interface Props extends DuetPageProps {
  nodeId: string;
  loudounNodeId?: string;
  utahNodeId?: string;
  arizonaNodeId?: string;
}

const DataCentersHubContent: FunctionComponent<Props> = ({
  nodeId,
  loudounNodeId = LOUDOUN_FEATURE_NODE_ID,
  utahNodeId = UTAH_FEATURE_NODE_ID,
  arizonaNodeId = ARIZONA_FEATURE_NODE_ID,
}) => {
  const queryData = useLazyLoadQuery<DataCentersHubQuery>(
    graphql`
      query DataCentersHubQuery(
        $nodeId: ID!
        $loudounNodeId: ID!
        $utahNodeId: ID!
        $arizonaNodeId: ID!
      ) {
        node(id: $nodeId) @required(action: THROW) {
          ... on FeedContentResourceInterface {
            authors {
              name
            }
            blocks {
              ... on CoreParagraphBlockType {
                __typename
                paragraphContents {
                  html
                }
                dropcap
              }
              ... on CoreHeadingBlockType {
                __typename
                contents {
                  html
                }
                headingId
                level
              }
              ... on CorePullquoteBlockType {
                __typename
                contents {
                  html
                }
                citation {
                  html
                }
              }
              ... on CoreSeparatorBlockType {
                __typename
              }
            }
            ...MetadataContainer_feedContentResource
            ...CskNavCommentCount_feedContentResource
          }
        }
        loudoun: node(id: $loudounNodeId) {
          ... on FeedContentResourceInterface {
            ...LoudounSlideMeta_feedContentResource
          }
        }
        utah: node(id: $utahNodeId) {
          ... on FeedContentResourceInterface {
            ...UtahSlideMeta_feedContentResource
          }
        }
        arizona: node(id: $arizonaNodeId) {
          ... on FeedContentResourceInterface {
            ...ArizonaSlideMeta_feedContentResource
          }
        }
      }
    `,
    { nodeId, loudounNodeId, utahNodeId, arizonaNodeId },
  );

  const { node, loudoun, utah, arizona } = queryData;
  const [activeSlideId, setActiveSlideId, slideReady] = useHubSlideState("note");
  const editorNoteBlocks = useMemo(
    () => editorNoteBlocksFromNode(node.blocks),
    [node],
  );

  const getDevWarnings = useMemo(
    () =>
      createGetDevWarnings(credits, {
        creditsSectionEnabled: sections.credits,
      }),
    [],
  );
  const stepRef = useRef<((delta: number) => void) | null>(null);
  const goToRef = useRef<((id: DeckSlideId) => void) | null>(null);
  const [stepBusy, setStepBusy] = useState(false);
  /** Note deep-link / mid-deck resume skipped title — chrome offers Intro. */
  const [introEscape, setIntroEscape] = useState(false);
  const hasNote = editorNoteBlocks.length > 0;

  return (
    <ArticleStoryChrome
      nodeId={nodeId}
      refreshLiveMetadata={refreshLiveMetadata}
      feedContentResource={node}
      skipTargetId={SKIP_TARGET_ID}
      resource={node}
      getDevWarnings={getDevWarnings}
      storyTheme={storyTheme}
    >
      <main className={clsx(globalStyles.main, fonts.main)}>
        <PackageNav
          activeSlideId={activeSlideId}
          hasNote={hasNote}
          stepBusy={stepBusy}
          onStep={(delta) => stepRef.current?.(delta)}
          showIntroReturn={
            hasNote && introEscape && activeSlideId !== "note"
          }
          onIntroReturn={() => goToRef.current?.("note")}
        />
        <div id={SKIP_TARGET_ID} className={globalStyles.stage}>
          {/* Withhold until session restore so the default note frame cannot flash. */}
          {slideReady ? (
            <SlideDeck
              activeSlideId={activeSlideId}
              onActiveSlideChange={setActiveSlideId}
              loudounMeta={loudoun}
              utahMeta={utah}
              arizonaMeta={arizona}
              editorNoteBlocks={editorNoteBlocks}
              stepRef={stepRef}
              goToRef={goToRef}
              onStepBusyChange={setStepBusy}
              onIntroEscapeChange={setIntroEscape}
            />
          ) : null}
        </div>
        <HubPrivacyBanner />
      </main>
    </ArticleStoryChrome>
  );
};

const DataCentersHub: FunctionComponent<Props> = (props) => (
  <>
    <Head>
      {/*
        Pin toolbar tint to black. Without this, iOS Safari samples the chapter
        accent (Loudoun yellow / Utah orange / Arizona cyan on package nav +
        ballast) and recolors the browser chrome mid-hop — that visualViewport
        churn coincided with freezes on repeated scroll.
      */}
      <meta name="theme-color" content="#000000" />
      <meta
        name="theme-color"
        content="#000000"
        media="(prefers-color-scheme: light)"
      />
      <meta
        name="theme-color"
        content="#000000"
        media="(prefers-color-scheme: dark)"
      />
      <link rel="stylesheet" href="https://use.typekit.net/umv4yht.css" />
    </Head>
    <ArticleStorySuspense
      storyTheme={storyTheme}
      loadingFallback={<ArticleLoadingFallback />}
    >
      <DataCentersHubContent {...props} />
    </ArticleStorySuspense>
  </>
);

export default DataCentersHub;
